import { getGroupedProductVariants } from "~/utilities/grouped-product-variants"
import { useQuery } from "@tanstack/vue-query"
import { ContentType } from "~/models/Content/ContentType"
import { useRoute as useNativeRoute } from "vue-router"

export const useProductVariantsStore = defineStore("products", () => {
  /** State */
  const licence = useLicences(60_000)

  const { cms } = useRuntimeConfig().public

  const getContent = useGetContent()

  const { data: productPages, suspense: waitForPages } = useQuery({
    queryKey: ["product-pages", cms.productsPagesLocationId] as const,
    staleTime: 1000 * 60 * 60, // 1 hour
    queryFn: ({ queryKey }) => getContent({
      childDepth: 1,
      contentType: [ContentType.ProductPage],
      parentLocationId: [parseInt(queryKey[1], 10)],
      sortField: "priority",
    }),
  })

  const { data: productVariantsRaw, suspense: waitForVariants } = useQuery({
    queryKey: ["product-variants", cms.productsLocationId] as const,
    staleTime: 1000 * 60 * 60, // 1 hour
    queryFn: ({ queryKey }) => getContent({
      contentType: [ContentType.Product],
      parentLocationId: [parseInt(queryKey[1], 10)],
    }),
  })

  const productsMap = computed(
    () => getGroupedProductVariants(
      productVariantsRaw.value,
      productPages.value,
      licence.data.value?.filter(isActive),
    ),
  )

  // FIXME: We should be able to use the auto-imported useRoute from Nuxt here, but for some reason reactivity breaks. Looks related to this issue: https://github.com/nuxt/nuxt/issues/28804
  const route = useNativeRoute<"variant">()

  /* Set active product and active variant */
  const activeProduct = computed(
    () => productsMap.value[route.params.product],
  )

  const lastActiveProduct = useLastActiveProduct()

  watch(activeProduct, value => {
    lastActiveProduct.value = value?.productKey ?? "exphil"
  })

  const activeVariant = computed(() => {
    const variants = activeProduct.value?.variants ?? []
    return variants.length === 1
      ? variants[0]
      : variants.find(v => v.variantKey === route.params.variant)
  })

  /* Create an implicit object in list format for easier use */
  const productsList = computed(() =>
    Object.values(productsMap.value ?? {}).sort((a, b) =>
      a.productTitle.localeCompare(b.productTitle),
    ),
  )

  return {
    activeVariant,
    activeProduct,
    productsList,
    productVariants: computed(() =>
      productsList.value.flatMap(p => p.variants),
    ),
    waitForProducts: () => Promise.all([
      waitForPages(),
      waitForVariants(),
    ]),
  }
})

if (import.meta.hot)
  import.meta.hot.accept(
    acceptHMRUpdate(useProductVariantsStore, import.meta.hot),
  )
